/** Equipo (1.0 admin Staff): quién es admin/staff y conceder o retirar el rol (`admin-grantRole`, auditado). */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, muted, Notice, str, Table, td } from '../lib/ui.js';

type Doc = Record<string, unknown>;

export function StaffPage(): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'user' | 'staff' | 'admin'>('staff');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    Promise.all([
      getDocs(query(collection(db, 'users'), where('role', '==', 'admin'))),
      getDocs(query(collection(db, 'users'), where('role', '==', 'staff'))),
    ])
      .then(([a, s]) => {
        setRows([...a.docs, ...s.docs].map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [version]);
  const grant = (target: { email?: string; uid?: string }, r: 'user' | 'staff' | 'admin'): void => {
    if (!window.confirm(`¿Poner el rol «${r}» a ${target.email ?? target.uid ?? ''}?`)) return;
    setError(null);
    call(api.admin.grantRole, { ...target, role: r })
      .then((res) => {
        setNotice(
          `Rol ${res.role} aplicado a ${res.uid} (antes: ${res.previous_role ?? 'user'}). La persona debe volver a entrar.`,
        );
        setEmail('');
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Equipo</h1>
      <p style={muted}>
        `admin` entra en este panel; `staff` queda reservado para soporte con permisos limitados. El
        rol es un custom claim: la persona debe cerrar sesión y volver a entrar para que aplique.
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      <form
        style={{ ...card, display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) grant({ email: email.trim().toLowerCase() }, role);
        }}
      >
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Email de la cuenta
          <input
            style={{ ...field, width: 280 }}
            type="email"
            value={email}
            required
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Rol
          <select
            style={field}
            value={role}
            onChange={(e) => {
              setRole(e.target.value as typeof role);
            }}
          >
            <option value="admin">admin</option>
            <option value="staff">staff</option>
            <option value="user">user (retirar)</option>
          </select>
        </label>
        <button type="submit" style={btn(true)}>
          Aplicar
        </button>
      </form>
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table head={['Usuario', 'Email', 'Rol', '']}>
          {rows.map((u) => (
            <tr key={str(u.id)}>
              <td style={td}>
                <Link to={`/usuarios/${str(u.id)}`}>@{str(u.username) || str(u.id).slice(-6)}</Link>
              </td>
              <td style={td}>{str(u.email)}</td>
              <td style={td}>{str(u.role)}</td>
              <td style={td}>
                <button
                  type="button"
                  style={btn(false, true)}
                  onClick={() => {
                    grant({ uid: str(u.id) }, 'user');
                  }}
                >
                  Retirar rol
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={4}>
                Nadie con rol todavía (usa `pnpm admin:grant`).
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}
